/**
 * A stored quantity as the org reads it: converted to the unit its choice shows (prefs.unitFor), rounded sensibly for
 * that unit, and written with the unit. `formatQuantity(1000, "°C", us)` is
 * `{ value: 1832, unit: "°F", text: "1832 °F" }`.
 *
 * Nothing stored changes. A unit the table does not know is shown as stored, the value as it is (to at most three
 * places); a value that is not a number reads "n/a" (a plain placeholder: no dash glyph stands in for a missing
 * value anywhere on this platform).
 */
import { convertBetween, dimensionOf, normalise } from "./convert";
import { unitFor, type DisplayPrefs } from "./prefs";

/** What a quantity is shown as. `value` is the number the text shows (rounded the same way); NaN when it is not a number. */
export interface Formatted {
  value: number;
  unit: string;
  text: string;
}

export interface FormatOptions {
  /** Places after the point, 0 to 10. Without it the unit's own count applies. */
  decimals?: number;
}

/** The places each display unit usually carries: a temperature is read in whole degrees, a kilopascal to a tenth. */
const DECIMALS: Record<string, number> = {
  "°c": 0, "°f": 0, k: 0,
  pa: 0, kpa: 1, mpa: 3, bar: 2, mbar: 0, psi: 1,
  kg: 1, g: 0, mg: 0, t: 3, lb: 1, oz: 1,
  mm: 1, cm: 1, m: 3, in: 2, ft: 2,
  ml: 0, l: 2, m3: 3,
  s: 0, sec: 0, min: 1, h: 2, hr: 2,
};

/** A temperature is never given extra places for being small: 0.4 °C is "0 °C". */
const FIXED = new Set(["°c", "°f", "k"]);

/** The most places a small value may add to its unit's usual count. */
const SMALL_EXTRA = 3;
/** The places an unknown unit's value may carry. */
const UNKNOWN_PLACES = 3;
const MAX_DECIMALS = 10;

const clampPlaces = (n: number) => Math.min(MAX_DECIMALS, Math.max(0, Math.round(n)));

/** The places this value is written with in this known unit: two figures of a value under one, within reason. */
function placesFor(unitKey: string, value: number, asked: number | undefined): number {
  if (asked !== undefined && Number.isFinite(asked)) return clampPlaces(asked);
  const base = DECIMALS[unitKey] ?? 2;
  const a = Math.abs(value);
  if (FIXED.has(unitKey) || a === 0 || a >= 1) return base;
  // The place of its first figure, then one more for the second.
  const wanted = Math.ceil(-Math.log10(a)) + 1;
  return Math.min(base + SMALL_EXTRA, Math.max(base, wanted));
}

/** A rounded number written with `places` after the point, never "-0". */
function written(value: number, places: number): string {
  const text = value.toFixed(places);
  return Number(text) === 0 ? text.replace(/^-/, "") : text;
}

export function formatQuantity(
  value: number,
  storedUnit: string,
  prefs: DisplayPrefs,
  opts: FormatOptions = {},
): Formatted {
  const stored = storedUnit.trim();
  const known = dimensionOf(stored) !== "";
  const unit = known ? unitFor(stored, prefs) : stored;
  if (!Number.isFinite(value)) return { value: Number.NaN, unit, text: "n/a" };

  if (!known) {
    // An unknown unit (or none) is shown as stored: the value as it is, to a few places and no more.
    const asked = opts.decimals !== undefined && Number.isFinite(opts.decimals);
    const fixed = written(value, asked ? clampPlaces(opts.decimals as number) : UNKNOWN_PLACES);
    const text = asked ? fixed : String(Number(fixed));
    return { value: Number(text), unit, text: unit === "" ? text : `${text} ${unit}` };
  }

  // unitFor chose a unit of the stored unit's own dimension, so this cannot refuse.
  const converted = convertBetween(value, stored, unit);
  const text = written(converted, placesFor(normalise(unit), converted, opts.decimals));
  return { value: Number(text), unit, text: `${text} ${unit}` };
}
